import { useState, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useFileBrowserStore } from '@/features/files/stores/fileBrowserStore'
import { api, getErrorMessage } from '@/shared/api/generated'
import type { ActiveUser } from '@/shared/api/generated'
import { toast } from '@/shared/stores/toastStore'

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [onlineUsers, setOnlineUsers] = useState<ActiveUser[]>([])
  const [showOnline, setShowOnline] = useState(false)
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()
  const configSiteName = useFileBrowserStore(s => s.config?.site_name)
  const [siteName, setSiteName] = useState(configSiteName || '')
  const [hasLogo, setHasLogo] = useState(false)

  useEffect(() => {
    if (configSiteName) { setSiteName(configSiteName); return }
    api.getBranding().then(b => {
      setSiteName(b.site_name)
      setHasLogo(b.has_logo)
      if (b.has_logo) {
        const link = document.querySelector("link[rel='icon']") || document.createElement('link')
        link.setAttribute('rel', 'icon')
        link.setAttribute('href', '/favicon.ico')
        document.head.appendChild(link)
      }
    }).catch(err => toast.error(getErrorMessage(err)))
  }, [configSiteName])

  const isAdmin = user?.isUserAdmin
  const canEditTagsOrVals = user?.permissions?.canEditTags || user?.permissions?.canEditVals

  useEffect(() => {
    if (!isAdmin) return
    const load = () => api.listActiveUsers().then(setOnlineUsers).catch(() => {})
    load()
    const interval = setInterval(load, 30000)
    return () => clearInterval(interval)
  }, [isAdmin])

  const handleLogout = () => {
    api.logout().catch(() => {}) // revoke server session (best-effort)
    logout()
    navigate('/login')
  }

  return (
    <header>
      <nav className="header-nav">
        {hasLogo && <img src="/branding/logo.svg" alt="" className="brand-logo" />}
        <span className="brand">{siteName || 'Cirrus'}</span>
        <NavLink to="/files" className={({ isActive }) => isActive ? 'active' : ''}>Files</NavLink>
        <NavLink to="/drive" className={({ isActive }) => isActive ? 'active' : ''}>Drive</NavLink>
        {isAdmin && (
          <NavLink to="/admin/site" className={({ isActive }) => isActive ? 'active' : ''}>User Admin</NavLink>
        )}
        {canEditTagsOrVals && (
          <NavLink to="/admin/tags" className={({ isActive }) => isActive ? 'active' : ''}>Tags</NavLink>
        )}
        <NavLink to="/settings" className={({ isActive }) => isActive ? 'active' : ''}>Settings</NavLink>
      </nav>

      <div className="user-menu">
        {isAdmin && onlineUsers.length > 0 && (
          <span
            className="online-indicator"
            data-testid="online-users"
            onClick={() => setShowOnline(!showOnline)}
            title="Users online"
          >
            {onlineUsers.reduce((sum, u) => sum + u.session_count, 0)} online
            {showOnline && (
              <div className="online-dropdown">
                {onlineUsers.map(u => (
                  <div key={u.username} className="online-user">
                    {u.username} ({u.session_count})
                  </div>
                ))}
              </div>
            )}
          </span>
        )}
        <button onClick={() => setMenuOpen(!menuOpen)}>
          <span>{user?.username}</span>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {menuOpen && (
          <>
            <div className="overlay" onClick={() => setMenuOpen(false)} />
            <div className="dropdown">
              <button onClick={handleLogout}>Logout</button>
            </div>
          </>
        )}
      </div>
    </header>
  )
}
